import React from 'react';
import { PiGlobeLight } from 'react-icons/pi';
import type { WebsiteStatus } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Link } from 'src/modules/shared/components/atoms/links/link';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getWebsiteUrlHost } from '../../utils/website-site-url.util';
import {
  WEBSITE_STATUS_LABEL_KEYS,
  WEBSITE_STATUS_PILL_VARIANTS,
} from '../../utils/website-status-labels.util';

/** State and actions of one header button. */
interface WebsiteSettingsHeaderAction {
  /** Whether the action is unavailable right now. */
  disabled: boolean;
  /** Whether the action is running. */
  loading: boolean;
  /** Runs the action. */
  onClick: () => void;
}

/** Props for the website settings header. */
interface WebsiteSettingsHeaderProps {
  /** Publication state of the website. */
  status: WebsiteStatus;
  /** Public URL of the live site, when it is published. */
  liveSiteUrl: string | null;
  /** Whether the editor holds changes the live site does not show. */
  unpublishedChanges: boolean;
  /** Whether a publication is queued or running. */
  deploying: boolean;
  /** Saves the draft without publishing. */
  saveAction: WebsiteSettingsHeaderAction;
  /** Opens a preview with real listings in a new tab. */
  previewAction: WebsiteSettingsHeaderAction;
  /** Saves and publishes the draft. */
  publishAction: WebsiteSettingsHeaderAction;
}

/**
 * Page header in the app's settings style: icon and title, the publication
 * status and live address underneath, and small actions on the right.
 * On phones the actions move to the bottom bar, which keeps the header short.
 * While a publication runs, the status line says so instead of listing
 * unpublished changes.
 *
 * @param props - Publication state and the three header actions.
 * @returns The page header.
 */
export const WebsiteSettingsHeader: React.FC<WebsiteSettingsHeaderProps> = ({
  status,
  liveSiteUrl,
  unpublishedChanges,
  deploying,
  saveAction,
  previewAction,
  publishAction,
}) => {
  const { t } = useI18n();
  const changesNote = deploying
    ? t('websites.settings.page.publishingChanges')
    : unpublishedChanges
      ? t('websites.settings.page.unpublishedChanges')
      : null;

  return (
    <PanelHeader
      className="px-4 py-4 sm:px-6"
      childrenClassName="min-w-0"
      actionsClassName="ml-auto hidden flex-wrap items-center justify-end gap-2 md:flex"
      actions={
        <>
          <Button
            className="hidden md:inline-flex"
            size="sm"
            variant="secondary"
            appearance="outline"
            onClick={previewAction.onClick}
            disabled={previewAction.disabled}
            loading={previewAction.loading}
          >
            {t('websites.settings.actions.previewDraft')}
          </Button>
          <Button
            className="hidden md:inline-flex"
            size="sm"
            variant="secondary"
            appearance="outline"
            onClick={saveAction.onClick}
            disabled={saveAction.disabled}
            loading={saveAction.loading}
          >
            {t('websites.settings.actions.saveDraft')}
          </Button>
          <Button
            className="hidden md:inline-flex"
            size="sm"
            onClick={publishAction.onClick}
            disabled={publishAction.disabled}
            loading={publishAction.loading}
          >
            {t(
              deploying
                ? 'websites.settings.actions.publishing'
                : 'websites.settings.actions.saveAndDeploy'
            )}
          </Button>
        </>
      }
    >
      <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
        <PiGlobeLight
          className="mt-1 h-8 w-8 shrink-0 sm:mt-0"
          aria-hidden="true"
        />
        <div className="min-w-0 space-y-1">
          <Text as="h2" size="h2" weight="medium">
            {t('websites.settings.title')}
          </Text>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <StatusPill
              label={t(WEBSITE_STATUS_LABEL_KEYS[status])}
              variant={WEBSITE_STATUS_PILL_VARIANTS[status]}
            />
            {liveSiteUrl ? (
              <Link href={liveSiteUrl} size="small" variant="neutral">
                {getWebsiteUrlHost(liveSiteUrl)}
              </Link>
            ) : (
              <Text size="small" color="secondary">
                {t('websites.settings.page.notPublishedYet')}
              </Text>
            )}
            {changesNote ? (
              <>
                <span aria-hidden="true" className="hidden sm:block">
                  <Text size="small" color="secondary">
                    ·
                  </Text>
                </span>
                <Text
                  className="w-full sm:w-auto"
                  size="small"
                  color="secondary"
                >
                  {changesNote}
                </Text>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </PanelHeader>
  );
};
